import { SYS_BUILD } from '@wwe-portal/web/utils/constants/environment';
import { getTrackerInstance } from './titan';

const getCorrectItemName = (name: null | string = '') => {
  if (!name) return name;
  const webCoinPattern = /(^web coins?$|^webcoins?$)/i;
  const rubyPattern = /^rub(y|ies)$/i;

  return rubyPattern.test(name)
    ? 'ruby'
    : webCoinPattern.test(name)
      ? 'web_coin'
      : name;
};

export const titanAnalyticsDefaultValues = async (currentUser: any = {}) => {
  return {
    sys_platform: 'web',
    event_source: 'web_portal',
    sys_build: SYS_BUILD,
    alliance_name: currentUser?.factionName?.trim() || null,
    league_level: currentUser.leagueId || null,
    // Suspected wrong source: this sends the game alias (`sc_<uuid>` / `fb_<id>`,
    // from users.playerId) as a *_level value on every Titan event — it reads like
    // a copy-paste of the line above. If player_level is numeric in the Titan
    // schema these events may be failing validation unnoticed. Left as-is because
    // the intended source is unknown; `sys_user_id` below already carries identity.
    player_level: currentUser.playerId || null,
    player_name:
      currentUser?.temporaryNickname?.trim() ||
      currentUser?.nickname?.trim() ||
      null,
    transaction_subtype_2: null,
    reference_id: null,
    reference_name: null,
    item_category: null,
    reference_global_event_id: null,
    reference_play_id: null,
    item_rarity: null,
    item_instance_id: null,
    reference_type: 'web',
    sys_user_id: currentUser?.userId || null,
  };
};

export const titanWebCurrencySpendBuyItem = async ({
  reference_subtype = null,
  item_id = null,
  item_name = null,
  item_quantity = null,
  item_balance = null,
  transaction_id = null,
  currentUser = {},
}) => {
  const Titan = getTrackerInstance().Titan;
  const defaultValues = await titanAnalyticsDefaultValues(currentUser);
  await Titan.SendEvent({
    name: 'sys.gti',
    parameters: {
      reference_subtype,
      transaction_type: 'spend',
      transaction_subtype: 'buy_item',
      reference_subtype_2: null,
      reference_subtype_3: reference_subtype,
      transaction_promo_id: null,
      reference_memo: 'web_currency_spend/buy_item',
      reference_event_id: null,
      item_id,
      item_name: getCorrectItemName(item_name),
      item_quantity,
      item_balance,
      transaction_id,
      ...defaultValues,
    },
  });
};

export const titanWebCurrencySpendEarnItem = async ({
  reference_subtype = null,
  item_id = null,
  item_name = null,
  item_quantity = null,
  item_balance = null,
  transaction_id = null,
  currentUser = {},
}) => {
  const Titan = getTrackerInstance().Titan;
  const defaultValues = await titanAnalyticsDefaultValues(currentUser);
  Titan.SendEvent({
    name: 'sys.gti',
    parameters: {
      reference_subtype,
      transaction_type: 'earn',
      transaction_subtype: 'earn_item',
      reference_subtype_2: null,
      reference_subtype_3: reference_subtype,
      transaction_promo_id: null,
      reference_memo: 'web_currency_spend/earn_item',
      reference_event_id: null,
      item_id,
      item_name: getCorrectItemName(item_name),
      item_quantity,
      item_balance,
      transaction_id,
      ...defaultValues,
    },
  });
};

export const titanFlashMarketBuyItemCanceledNotEnough = async ({
  reference_subtype = null,
  transaction_subtype = null,
  reference_subtype_2 = null,
  reference_memo = null,
  item_id = null,
  item_name = null,
  item_quantity = null,
  item_balance = null,
  transaction_id = null,
  currentUser = {},
}) => {
  const Titan = getTrackerInstance().Titan;
  const defaultValues = await titanAnalyticsDefaultValues(currentUser);
  Titan.SendEvent({
    name: 'sys.gti',
    parameters: {
      reference_subtype,
      transaction_type: 'spend',
      transaction_subtype,
      reference_subtype_2,
      reference_subtype_3: reference_subtype,
      transaction_promo_id: null,
      reference_memo,
      reference_event_id: null,
      item_id,
      item_name: getCorrectItemName(item_name),
      item_quantity,
      item_balance,
      transaction_id,
      ...defaultValues,
    },
  });
};

export const titanWebCurrencySpendReroll = async ({
  reference_subtype = null,
  transaction_subtype = null,
  reference_memo = null,
  item_id = null,
  item_name = null,
  item_quantity = null,
  item_balance = null,
  transaction_id = null,
  currentUser = {},
}) => {
  const Titan = getTrackerInstance().Titan;
  const defaultValues = await titanAnalyticsDefaultValues(currentUser);
  Titan.SendEvent({
    name: 'sys.gti',
    parameters: {
      reference_subtype,
      transaction_type: 'spend',
      transaction_subtype,
      reference_subtype_2: null,
      reference_subtype_3: reference_subtype,
      transaction_promo_id: null,
      reference_memo,
      reference_event_id: null,
      item_id,
      item_name: getCorrectItemName(item_name),
      item_quantity,
      item_balance,
      transaction_id,
      ...defaultValues,
    },
  });
};

export const titanFlashMarketRerollCanceled = async ({
  reference_subtype_2 = null,
  item_id = null,
  item_name = null,
  item_quantity = null,
  item_balance = null,
  transaction_id = null,
  currentUser = {},
}) => {
  const Titan = getTrackerInstance().Titan;
  const defaultValues = await titanAnalyticsDefaultValues(currentUser);
  Titan.SendEvent({
    name: 'sys.gti',
    parameters: {
      reference_subtype: 'flash_market',
      transaction_type: 'spend',
      transaction_subtype: `not_enough_${reference_subtype_2}_coins`,
      reference_subtype_2,
      reference_subtype_3: 'flash_market',
      transaction_promo_id: null,
      reference_memo: `web_flash_market_re_roll_canceled/not_enough_${reference_subtype_2}_coins`,
      reference_event_id: null,
      item_id,
      item_name: getCorrectItemName(item_name),
      item_quantity,
      item_balance,
      transaction_id,
      ...defaultValues,
    },
  });
};

export const titanWebPortalIapBuy = async ({
  reference_subtype = null,
  reference_subtype_2 = null,
  transaction_subtype = null,
  transaction_promo_id = null,
  item_id = null,
  item_name = null,
  item_quantity = null,
  item_balance = null,
  transaction_id = null,
  currentUser = {},
}) => {
  const Titan = getTrackerInstance().Titan;
  const defaultValues = await titanAnalyticsDefaultValues(currentUser);
  Titan.SendEvent({
    name: 'sys.gti',
    parameters: {
      reference_subtype,
      transaction_type: 'buy',
      transaction_subtype,
      reference_subtype_2,
      reference_subtype_3: reference_subtype,
      transaction_promo_id,
      reference_memo: `web_portal_iap/buy`,
      reference_event_id: null,
      item_id,
      item_name: getCorrectItemName(item_name),
      item_quantity,
      item_balance,
      transaction_id,
      ...defaultValues,
    },
  });
};

/**
 * Titan Send Sys.Payment event
 * @param success {boolean}
 * @param error {string}
 * @param context {popup_id|section} => {496|web_coins}
 * @param tenant {string | userId}
 * @param amount_us {number | 4.99}
 * @param amount_us_converted N/A
 * @param amount_local {number | 4.99}
 * @param local_currency_type {string | USD}
 * @param special {string | Example: $$20159}
 * @param special_id {string | Example: Offer_HC_Pack_1}
 * @param store_sku {string | com.scopely.whiplash.iap_basic_499}
 * @param game_sku {string | NoCashOffer199}
 * @param payment_id {number | 1142341732}
 * @param season_id {string | Example: R37_Section_Charging}
 * @param transaction_id {string uuid| d60518f5-8b0e-4e25-b9f2-d1bfebc5e65e}
 * @param sku_type {string | ["standard", "bundle", "sale"]}
 * @param is_discounted_package {boolean | true}
 * @param discount_info {object | discount_id, original_amount_local, original_amount_us}
 * @param section_title {string | Example: $$30001}
 * @param section_id {string | Example: R37_Section_Charging}
 * @param section_weight { number | 1}
 * @param section_color {string | Example: Green}
 * @param offer_weight {number | 2006033}
 */
const sendSysPayment = ({
  success = false,
  error = false,
  context = null,
  tenant = null,
  amount_us = null,
  amount_us_converted = null,
  amount_local = null,
  local_currency_type = 'USD',
  special = null,
  special_id = null,
  store_sku = null,
  game_sku = null,
  payment_id = null,
  season_id = null,
  transaction_id = null,
  sku_type = 'Standard',
  is_discounted_package = null,
  discount_info = {
    discount_id: null,
    original_amount_local: 0,
    original_amount_us: 0,
  },
  section_title = null,
  section_id = null,
  section_weight = null,
  section_color = null,
  offer_weight = null,
}) => {
  const Titan = getTrackerInstance().Titan;
  Titan.SendEvent({
    name: 'sys.payment',
    parameters: {
      success,
      error,
      context,
      tenant,
      amount_us,
      amount_us_converted,
      amount_local,
      local_currency_type,
      special,
      special_id,
      store_sku,
      game_sku,
      payment_id,
      season_id,
      transaction_id,
      sku_type,
      is_discounted_package,
      discount_info,
      section_title,
      section_id,
      section_weight,
      section_color,
      offer_weight,
    },
  });
};

/**
 * Titan Send sys.game_transaction event
 * @param context {string | 10_6star_econ_league_16_tp}
 * @param event_source {string | web_portal}
 * @param item_balance {number | Balance of the item}
 * @param item_id {string | web_currency_web_coin, ruby}
 * @param item_name {string | Name of the item}
 * @param quantity {number | 1}
 * @param reference_id {string | news, events}
 * @param sys_platform {string | web}
 * @param sys_user_id {string | userId of session}
 * @param trans_desc {string | Example: dashboard, leaderboard, special_offers}
 * @param trans_type {string | web_portal_iap}
 * @param transaction_id {string uuid}
 */
export const sendSysGameTransaction = ({
  context = null,
  event_source = null,
  item_balance = null,
  item_id = null,
  item_name = null,
  quantity = null,
  reference_id = null,
  sys_platform = null,
  sys_user_id = null,
  trans_desc = null,
  trans_type = null,
  transaction_id = null,
  extra_params = {},
}: any) => {
  const Titan = getTrackerInstance().Titan;
  Titan.SendEvent({
    name: 'sys.game_transaction',
    parameters: {
      context,
      event_source,
      item_balance,
      item_id,
      item_name,
      quantity,
      reference_id,
      sys_platform,
      sys_user_id,
      trans_desc,
      trans_type,
      transaction_id,
      ...extra_params,
    },
  });
};

/**
 * Titan Send sys.gti event
 * @param alliance_name {string | Example: BroTeam}
 * @param event_source {string | Example: web_portal}
 * @param item_id {string | web_currency_web_coin}
 * @param item_name {string | web_coin}
 * @param item_quantity {number | 10}
 * @param item_balance {number | 10}
 * @param league_level {number | 17}
 * @param player_level {string | DotNV9xx}
 * @param player_name {string | Champion11}
 * @param reference_memo {string | }
 * @param reference_subtype {string | web_portal_iap/earn_currency_exchange_reward}
 * @param reference_subtype_2 {string | varun_stepup_both_01}
 * @param reference_subtype_3 {string | special_offers}
 * @param reference_type {string | web}
 * @param sys_build {string | QA}
 * @param sys_platform {string | web}
 * @param sys_user_id {string | uuid}
 * @param transaction_id {number | 1290245198}
 * @param transaction_promo_id {string | varun_stepup_both_01}
 * @param transaction_subtype {string | earn_currency_exchange_reward}
 * @param transaction_type {string | earn}
 */
const sendSysGti = ({
  alliance_name = null,
  item_id = null,
  item_name = null,
  item_quantity = null,
  item_balance = null,
  league_level = null,
  player_level = null,
  player_name = null,
  reference_memo = null,
  reference_subtype = null,
  reference_subtype_2 = null,
  reference_subtype_3 = null,
  reference_type = null,
  sys_user_id = null,
  transaction_id = null,
  transaction_promo_id = null,
  transaction_subtype = null,
  transaction_type = null,
}) => {
  const Titan = getTrackerInstance().Titan;
  Titan.SendEvent({
    name: 'sys.gti',
    parameters: {
      alliance_name,
      event_source: 'web_portal',
      item_id,
      item_name,
      item_quantity,
      item_balance,
      league_level,
      player_level,
      player_name,
      reference_memo,
      reference_subtype,
      reference_subtype_2,
      reference_subtype_3,
      reference_type,
      sys_platform: 'web',
      sys_user_id,
      transaction_id,
      transaction_promo_id,
      transaction_subtype,
      transaction_type,
    },
  });
};

/**
 * Titan Send sys.ui event
 * @param flow_name {string | Example: web_store_telemetric}
 * @param index {number | Example: 108}
 * @param interaction_type {string | tap}
 * @param interaction_object {string | go_to_next_button}
 * @param previous_screen_category {string | dashboard}
 * @param screen_category {string | special_offers}
 * @param screen_content { object | go_to_offer_id, go_to_position, offer_id, position, total_banner_slots }
 * @param screen_id {number | 1}
 * @param screen_name {string | special_offers}
 * @param screen_subcategory {string | offer_banner}
 * @param screen_type {string | screen}
 * @param previous_screen_id {string | varun_stepup_both_01}
 * @param previous_screen_name {string | special_offers}
 * @param interaction_object_category {string}
 * @param flow_order {string | QA}
 * @param session {string | uuid}
 * @param sys_user_id {string | uuid}
 * @param sys_platform {string | web}
 * @param sys_os_s {string | Windows}
 */
const sendSysUI = ({
  index = null,
  flow_name = null,
  interaction_type = null,
  interaction_object = null,
  previous_screen_category = null,
  screen_category = null,
  screen_content = null,
  screen_id = null,
  screen_name = null,
  screen_subcategory = null,
  screen_type = null,
  previous_screen_id = null,
  previous_screen_name = null,
  interaction_object_category = null,
  flow_order = null,
  session = null,
  sys_user_id = null,
  sys_platform = null,
  sys_os_s = null,
}) => {
  const Titan = getTrackerInstance().Titan;
  Titan.SendEvent({
    name: 'sys.ui',
    parameters: {
      flow_name,
      index,
      interaction_type,
      interaction_object,
      previous_screen_category,
      screen_category,
      screen_content,
      screen_id,
      screen_name,
      screen_subcategory,
      screen_type,
      previous_screen_id,
      previous_screen_name,
      interaction_object_category,
      flow_order,
      session,
      sys_user_id,
      sys_platform,
      sys_os_s,
    },
  });
};

const sendGameTransactionUpsell = ({
  context,
  item_balance = null,
  item_id,
  item_name,
  quantity,
  reference_id,
  sys_user_id,
  trans_desc,
  trans_type,
  transaction_id,
}: any) => {
  sendSysGameTransaction({
    context,
    item_balance,
    item_id,
    item_name,
    quantity,
    reference_id,
    sys_user_id,
    trans_desc,
    trans_type,
    transaction_id,
    event_source: 'web_portal',
    sys_platform: 'web',
  });
};

/**
 * @deprecated use analytics functions from "analytics/common"
 */
export const transformPayment = {
  sendSysPayment,
  sendSysGameTransaction,
  sendSysGti,
  sendSysUI,
  sendGameTransactionUpsell,
};
